import { useEffect, useState } from "react";
import { addDays, parseISO } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHead } from "@/pages/admin/AdminLayout";
import { BookingLine } from "@/pages/admin/Overview";
import { api, gbp, longDate, isoDate, formatApiError } from "@/lib/api";

const StatusSelect = ({ b, onChange }) => (
  <Select value={b.status} disabled={b.status === "cancelled"} onValueChange={async (v) => {
    if (v === "cancelled" && !window.confirm(`Cancel this booking and refund ${gbp(b.amount)} to ${b.patient_name}?`)) return;
    try {
      const { data } = await api.patch(`/admin/bookings/${b.id}`, { status: v });
      toast.success(data.refund_status === "refunded" ? `Cancelled — ${gbp(data.refund_amount)} refunded` : "Status updated");
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
  }}>
    <SelectTrigger className="w-36 h-8 text-xs" data-testid={`admin-booking-status-select-${b.id}`}><SelectValue /></SelectTrigger>
    <SelectContent>
      {["confirmed", "completed", "no_show", "cancelled"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}
    </SelectContent>
  </Select>
);

export default function Appointments() {
  const [mode, setMode] = useState("day");
  const [day, setDay] = useState(isoDate(new Date()));
  const [list, setList] = useState([]);
  const [pracs, setPracs] = useState([]);

  const load = () => {
    const params = mode === "day" ? { date_from: day, date_to: day } : { date_from: isoDate(new Date()) };
    api.get("/admin/bookings", { params }).then((r) => setList(r.data));
  };
  useEffect(load, [mode, day]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/admin/practitioners").then((r) => setPracs(r.data)); }, []);

  const shift = (n) => setDay(isoDate(addDays(parseISO(day), n)));
  const grouped = list.reduce((acc, b) => ({ ...acc, [b.date]: [...(acc[b.date] || []), b] }), {});

  return (
    <div className="fade-up">
      <PageHead eyebrow="Schedule" title="Appointments">
        <Tabs value={mode} onValueChange={setMode}>
          <TabsList>
            <TabsTrigger value="day" data-testid="appointments-mode-day">Day</TabsTrigger>
            <TabsTrigger value="upcoming" data-testid="appointments-mode-upcoming">All upcoming</TabsTrigger>
          </TabsList>
        </Tabs>
      </PageHead>
      {mode === "day" ? (
        <>
          <div className="flex items-center gap-3 mb-5">
            <Button variant="outline" size="icon" onClick={() => shift(-1)} data-testid="appointments-prev-day"><ChevronLeft className="h-4 w-4" /></Button>
            <p className="font-display font-bold min-w-[200px] text-center" data-testid="appointments-current-day">{longDate(day)}</p>
            <Button variant="outline" size="icon" onClick={() => shift(1)} data-testid="appointments-next-day"><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" onClick={() => setDay(isoDate(new Date()))} data-testid="appointments-today">Today</Button>
          </div>
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(260px, 1fr))` }}>
            {pracs.map((p) => {
              const items = list.filter((b) => b.practitioner_id === p.id);
              return (
                <div key={p.id} className="rounded-2xl bg-card border border-border p-4" data-testid={`schedule-column-${p.id}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <img src={p.image} alt={p.name} className="h-9 w-9 rounded-lg object-cover" />
                    <div><p className="font-display font-bold text-sm">{p.name}</p><p className="text-xs text-muted-foreground">{items.length} appointments</p></div>
                  </div>
                  {items.length === 0 ? <p className="text-xs text-muted-foreground py-3">Free all day</p> : items.map((b) => (
                    <BookingLine key={b.id} b={b} right={<StatusSelect b={b} onChange={load} />} />
                  ))}
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="space-y-6">
          {Object.keys(grouped).length === 0 && <p className="text-muted-foreground" data-testid="appointments-empty">No upcoming appointments.</p>}
          {Object.entries(grouped).map(([d, items]) => (
            <div key={d} className="rounded-2xl bg-card border border-border p-5">
              <p className="font-display font-bold mb-2">{longDate(d)}</p>
              {items.map((b) => <BookingLine key={b.id} b={b} right={<StatusSelect b={b} onChange={load} />} />)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
